// src/sizing/newSizingEngine.ts

import { runMonteCarloFast, runMonteCarloRefine, type McInput } from '../engine/mcEngine';
import {
  sizeKvSimplePooled,
  sizeKvBinPackPooled,
  sizeKvPersonaDedicated,
  type KvSizingResult,
} from './kvSizing';
import { MC_CURVE_PERCENTILES, type McCurvePercentile } from '@/types/percentiles';

const DEFAULT_HORIZON_SEC = 600;
const DEFAULT_FAST_TRIALS = 10;
const DEFAULT_REFINE_TRIALS = 50;
const UTILIZATION_TARGET = 1;

// SLO-related defaults
const DEFAULT_UI_OVERHEAD_MS = 250; // 0.25 seconds of net/UI
const DEFAULT_SLO_ALPHA = 0;        // 0 = continuity only, 1 = fully SLO driven
const DEFAULT_SLO_MAX_PENALTY = 4;  // cap how aggressive SLO can inflate GPU counts

export interface NewSizingSnapshot {
  // Latency curves from MC
  p50Active: number;
  p95Active: number;
  activeByPercentile: Record<McCurvePercentile, number>;

  // KV retention (sessions)
  kvSessionsP50: number;
  kvSessionsP95: number;
  kvSessionsByPercentile: Record<McCurvePercentile, number>;
  kvGiBByPercentile: Record<McCurvePercentile, number>;
  kvGpusByPercentile: Record<McCurvePercentile, number>;

  // KV memory usage
  kvBytesPerToken: number;
  kvGiBP50: number;
  kvGiBP95: number;
  kvGpusP50: number;
  kvGpusP95: number;

  // Throughput
  avgPromptTokensPerSec?: number;
  avgDecodeTokensPerSec?: number;
  requiredPromptTps: number;
  requiredDecodeTps: number;
  throughputByPercentile: Record<
    McCurvePercentile,
    {
      requiredPromptTps: number;
      requiredDecodeTps: number;
      promptGpus: number;
      decodeGpus: number;
    }
  >;
  promptTpsPerGpu: number;
  decodeTpsPerGpu: number;
  promptGpus: number;
  decodeGpus: number;

  // Final bottleneck
  finalGpuCount: number;
  finalBottleneck: 'kv' | 'prompt' | 'decode' | 'none';
  finalByPercentile: Record<
    McCurvePercentile,
    {
      finalGpuCount: number;
      bottleneck: 'kv' | 'prompt' | 'decode' | 'none';
    }
  >;

  // Metadata
  modelId: string;
  precision: number;
  horizonSec: number;
  trials: number;
}

export interface NewSizingRequest {
  baseConcurrentUsers: number;
  conversationTurns: number;

  batchSize: number;
  latencySloMs: number;
  perGpuPromptTps: number;
  perGpuDecodeTps: number;

  personas: Array<{
    id: string;
    name: string;
    sharePercent: number;
    population: number;
    inputTokensPerConversation: number;
    outputTokensPerConversation: number;
    readSeconds: number;
    thinkSeconds: number;
    ttftMs: number;
    decodeTps: number;
    turnsKept?: number;
    latencyTargetMs?: number;
  }>;

  model: {
    id: string;
    paramsBillions: number;
    precisionBits: number;
    kvBytesPerToken: number;
    weightsGiB: number;
    weightsGiBUnshared?: number;
    usableKvGiBPerGpu: number;
    hbmGiB?: number;
    bridgeGroupSize?: number;
    fitsWeightsWithoutSharing?: boolean;
    fitsWeightsWithSharing?: boolean;
    weightsSharedWithinGroup?: boolean;
  };

  throughput: {
    promptTpsPerGpu: number;
    decodeTpsPerGpu: number;
  };

  mcConfig?: {
    horizonSeconds?: number;
    fastTrials?: number;
    refineTrials?: number;
    trafficVolatility?: number;
  };

  sloConfig?: {
    alpha?: number;
    uiOverheadMs?: number;
    maxPenalty?: number;
  };
}

export function runNewSizingEngine(request: NewSizingRequest): NewSizingSnapshot {
  const horizonSec = request.mcConfig?.horizonSeconds ?? DEFAULT_HORIZON_SEC;
  const fastTrials = request.mcConfig?.fastTrials ?? DEFAULT_FAST_TRIALS;
  const refineTrials = request.mcConfig?.refineTrials ?? DEFAULT_REFINE_TRIALS;

  const sloAlpha = request.sloConfig?.alpha ?? DEFAULT_SLO_ALPHA;
  const uiOverheadSec = (request.sloConfig?.uiOverheadMs ?? DEFAULT_UI_OVERHEAD_MS) / 1000;
  const maxSloPenalty = request.sloConfig?.maxPenalty ?? DEFAULT_SLO_MAX_PENALTY;
  const targetE2eSecGlobal = request.latencySloMs > 0 ? request.latencySloMs / 1000 : 0;

  // 1) Build MC input
  const mcInput: McInput = {
    personas: request.personas.map((p) => {
      const turnsKept = p.turnsKept && p.turnsKept > 0 ? p.turnsKept : 1;
      const promptTokensPerTurn = p.inputTokensPerConversation / turnsKept;
      const decodeTokensPerTurn = p.outputTokensPerConversation / turnsKept;
      const totalTokensPerTurn = promptTokensPerTurn + decodeTokensPerTurn;
      const ttftSec = (p.ttftMs ?? 0) / 1000;
      const decodeSec = p.decodeTps > 0 ? totalTokensPerTurn / p.decodeTps : 0;
      const serviceTimeSec = ttftSec + decodeSec;

      return {
        personaId: p.id,
        population: p.population,
        tokensPerTurn: {
          input: promptTokensPerTurn,
          output: decodeTokensPerTurn,
          total: totalTokensPerTurn,
        },
        dutyCycle: {
          readTimeSec: p.readSeconds,
          thinkTimeSec: p.thinkSeconds,
        },
        service: {
          ttftMs: p.ttftMs,
          decodeTokPerSec: p.decodeTps,
          serviceTimeSec,
        },
      };
    }),
    concurrentUsersTotal: request.baseConcurrentUsers,
    conversationsPerUser: request.conversationTurns,
    latencySloMs: request.latencySloMs,
    batchSize: request.batchSize,
    trials: refineTrials,
    durationSec: horizonSec,
    trafficVolatility: request.mcConfig?.trafficVolatility,
  };

  console.log('[NewEngine] MC inputs', {
    horizonSec,
    fastTrials,
    refineTrials,
    concurrentUsersTotal: mcInput.concurrentUsersTotal,
    trafficVolatility: mcInput.trafficVolatility,
    personas: mcInput.personas.map((p) => ({
      id: p.personaId,
      population: p.population,
      tokensPerTurn: p.tokensPerTurn,
      read: p.dutyCycle.readTimeSec,
      think: p.dutyCycle.thinkTimeSec,
      ttftMs: p.service?.ttftMs,
      decodeTokPerSec: p.service?.decodeTokPerSec,
      serviceTimeSec: p.service?.serviceTimeSec,
    })),
    sloAlpha,
    targetE2eSecGlobal,
    uiOverheadSec,
    maxSloPenalty,
  });

  const fastResult = runMonteCarloFast(
    { ...mcInput, trials: fastTrials },
    'new-engine',
    request.model.id,
  );

  const refineResult = runMonteCarloRefine(
    { ...mcInput, trials: refineTrials },
    'new-engine',
    request.model.id,
  );

  const activeByPercentile: Record<McCurvePercentile, number> = {
    5:
      refineResult.activePercentiles?.[5] ??
      fastResult.activePercentiles?.[5] ??
      0,
    25:
      refineResult.activePercentiles?.[25] ??
      fastResult.activePercentiles?.[25] ??
      0,
    50:
      refineResult.p50TotalActive ??
      refineResult.activePercentiles?.[50] ??
      fastResult.p50TotalActive ??
      fastResult.activePercentiles?.[50] ??
      0,
    75:
      refineResult.activePercentiles?.[75] ??
      fastResult.activePercentiles?.[75] ??
      0,
    95:
      refineResult.p95TotalActive ??
      refineResult.activePercentiles?.[95] ??
      fastResult.p95TotalActive ??
      fastResult.activePercentiles?.[95] ??
      0,
  };

  const p50Active = activeByPercentile[50];
  const p95Active = activeByPercentile[95];

  const personaStatsMap = new Map<
    string,
    { p50: number; p95: number; percentiles?: Record<number, number> }
  >(
    (refineResult.personas ?? fastResult.personas ?? []).map((s) => [
      s.personaId,
      {
        p50: s.p50ActiveSessions,
        p95: s.p95ActiveSessions,
        percentiles: s.percentiles,
      },
    ]),
  );

  console.log('[NewEngine] MC active sessions (throughput curve)', {
    p50Active,
    p95Active,
  });

  const ratioActive = p50Active > 0 ? p95Active / p50Active : 1;

  // 3) KV sizing
  const mcSummary = {
    p50Active,
    p95Active,
    activePercentiles: activeByPercentile,
    personas: request.personas.map((p) => ({
      personaId: p.id,
      inputTokensPerConversation: p.inputTokensPerConversation,
      outputTokensPerConversation: p.outputTokensPerConversation,
      shareOfTraffic: (p.sharePercent ?? 0) / 100,
    })),
  };

  const kvSizing = sizeKvBinPackPooled(request, mcSummary);

  const {
    kvSessionsP50,
    kvSessionsP95,
    kvSessionsByPercentile,
    avgTokensPerConversation,
    kvBytesPerToken,
    kvGiBP50,
    kvGiBP95,
    kvCapacityGiB,
    kvGpusP50,
    kvGpusP95,
    kvGiBByPercentile,
    kvGpusByPercentile,
  }: KvSizingResult = kvSizing;

  console.log('[NewEngine] KV sizing (full retention)', {
    baseConcurrentUsers: request.baseConcurrentUsers,
    p50Active,
    p95Active,
    ratioActive,
    kvSessionsP50,
    kvSessionsP95,
    avgTokensPerConversation,
    kvGiBP50,
    kvGiBP95,
    kvGpusP50,
    kvGpusP95,
  });

  // 4) Throughput sizing

  const throughputByPercentile: Record<
    McCurvePercentile,
    {
      requiredPromptTps: number;
      requiredDecodeTps: number;
      promptGpus: number;
      decodeGpus: number;
    }
  > = {
    5: { requiredPromptTps: 0, requiredDecodeTps: 0, promptGpus: 0, decodeGpus: 0 },
    25: { requiredPromptTps: 0, requiredDecodeTps: 0, promptGpus: 0, decodeGpus: 0 },
    50: { requiredPromptTps: 0, requiredDecodeTps: 0, promptGpus: 0, decodeGpus: 0 },
    75: { requiredPromptTps: 0, requiredDecodeTps: 0, promptGpus: 0, decodeGpus: 0 },
    95: { requiredPromptTps: 0, requiredDecodeTps: 0, promptGpus: 0, decodeGpus: 0 },
  };

  const promptTpsPerGpu = request.perGpuPromptTps ?? 0;
  const decodeTpsPerGpu = request.perGpuDecodeTps ?? 0;
  const avgPromptTokensPerSec =
    refineResult.avgPromptTokensPerSec ?? fastResult.avgPromptTokensPerSec ?? 0;
  const avgDecodeTokensPerSec =
    refineResult.avgDecodeTokensPerSec ?? fastResult.avgDecodeTokensPerSec ?? 0;

  type PersonaThroughputInputs = {
    promptTokensPerTurn: number;
    decodeTokensPerTurn: number;
    perSessionPromptTps: number;
    perSessionDecodeTps: number;
  };

  const personaThroughputInputs = new Map<string, PersonaThroughputInputs>();
  request.personas.forEach((p) => {
    const turnsKept = p.turnsKept && p.turnsKept > 0 ? p.turnsKept : 1;
    const promptTokensPerTurn = p.inputTokensPerConversation / turnsKept;
    const decodeTokensPerTurn = p.outputTokensPerConversation / turnsKept;

    // Prefill SLO driven prompt TPS; default to latency SLO when ttft is absent.
    const prefillSec = Math.max((p.ttftMs ?? request.latencySloMs ?? 0) / 1000, 1);
    const perSessionPromptTps = promptTokensPerTurn / prefillSec;
    const perSessionDecodeTps = p.decodeTps && p.decodeTps > 0 ? p.decodeTps : 0;

    personaThroughputInputs.set(p.id, {
      promptTokensPerTurn,
      decodeTokensPerTurn,
      perSessionPromptTps,
      perSessionDecodeTps,
    });
  });

  MC_CURVE_PERCENTILES.forEach((percentile) => {
    let requiredPromptTps = 0;
    let requiredDecodeTps = 0;

    request.personas.forEach((persona) => {
      const stats = personaStatsMap.get(persona.id);
      const inputs = personaThroughputInputs.get(persona.id);
      if (!stats || !inputs) return;

      const activeAtPercentile =
        stats.percentiles?.[percentile] ??
        (percentile === 50 ? stats.p50 : percentile === 95 ? stats.p95 : 0) ??
        0;

      if (activeAtPercentile > 0) {
        requiredPromptTps += activeAtPercentile * inputs.perSessionPromptTps;
        requiredDecodeTps += activeAtPercentile * inputs.perSessionDecodeTps;
      }
    });

    const adjustedPromptTps =
      UTILIZATION_TARGET > 0 ? requiredPromptTps / UTILIZATION_TARGET : requiredPromptTps;
    const adjustedDecodeTps =
      UTILIZATION_TARGET > 0 ? requiredDecodeTps / UTILIZATION_TARGET : requiredDecodeTps;

    const promptGpus =
      promptTpsPerGpu > 0 ? Math.ceil(adjustedPromptTps / promptTpsPerGpu) : 0;
    const decodeGpus =
      decodeTpsPerGpu > 0 ? Math.ceil(adjustedDecodeTps / decodeTpsPerGpu) : 0;

    throughputByPercentile[percentile] = {
      requiredPromptTps: adjustedPromptTps,
      requiredDecodeTps: adjustedDecodeTps,
      promptGpus,
      decodeGpus,
    };
  });

  const requiredPromptTps = throughputByPercentile[95]?.requiredPromptTps ?? 0;
  const requiredDecodeTps = throughputByPercentile[95]?.requiredDecodeTps ?? 0;

  const promptGpus = throughputByPercentile[95]?.promptGpus ?? 0;
  const decodeGpus = throughputByPercentile[95]?.decodeGpus ?? 0;

  console.log('[NewEngine] Throughput sizing from MC percentiles', {
    avgPromptTokensPerSec,
    avgDecodeTokensPerSec,
    throughputByPercentile,
    requiredPromptTpsP95: requiredPromptTps,
    requiredDecodeTpsP95: requiredDecodeTps,
    promptTpsPerGpu,
    decodeTpsPerGpu,
    promptGpusP95: promptGpus,
    decodeGpusP95: decodeGpus,
    utilizationTarget: UTILIZATION_TARGET,
    horizonSec,
    trials: refineTrials,
  });

  const finalByPercentile: NewSizingSnapshot['finalByPercentile'] = {
    5: { finalGpuCount: 0, bottleneck: 'none' },
    25: { finalGpuCount: 0, bottleneck: 'none' },
    50: { finalGpuCount: 0, bottleneck: 'none' },
    75: { finalGpuCount: 0, bottleneck: 'none' },
    95: { finalGpuCount: 0, bottleneck: 'none' },
  };

  MC_CURVE_PERCENTILES.forEach((percentile) => {
    const kvGpus = kvGpusByPercentile[percentile] ?? 0;
    const promptGpusP = throughputByPercentile[percentile]?.promptGpus ?? 0;
    const decodeGpusP = throughputByPercentile[percentile]?.decodeGpus ?? 0;

    const finalGpuCount = Math.max(kvGpus, promptGpusP, decodeGpusP);
    let bottleneck: NewSizingSnapshot['finalBottleneck'] = 'none';
    if (finalGpuCount === kvGpus) {
      bottleneck = 'kv';
    } else if (finalGpuCount === promptGpusP) {
      bottleneck = 'prompt';
    } else if (finalGpuCount === decodeGpusP) {
      bottleneck = 'decode';
    }

    finalByPercentile[percentile] = {
      finalGpuCount,
      bottleneck,
    };
  });

  const finalGpuCount = finalByPercentile[95].finalGpuCount;
  const finalBottleneck = finalByPercentile[95].bottleneck;

  const snapshot: NewSizingSnapshot = {
    p50Active,
    p95Active,
    activeByPercentile,

    kvSessionsP50,
    kvSessionsP95,
    kvSessionsByPercentile,
    kvGiBByPercentile,
    kvGpusByPercentile,

    kvBytesPerToken,
    kvGiBP50,
    kvGiBP95,
    kvGpusP50,
    kvGpusP95,

    avgPromptTokensPerSec,
    avgDecodeTokensPerSec,
    requiredPromptTps,
    requiredDecodeTps,
    throughputByPercentile,
    promptTpsPerGpu,
    decodeTpsPerGpu,
    promptGpus,
    decodeGpus,

    finalGpuCount,
    finalBottleneck,
    finalByPercentile,

    modelId: request.model.id,
    precision: request.model.precisionBits,
    horizonSec,
    trials: refineTrials,
  };

  console.log('[NewEngine] Final sizing snapshot', snapshot);

  return snapshot;
}
